"use client";

import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAlertStore } from "@/components/context/alert-store";
import AlertComponent from "@/components/core/alert";
import { Trans } from "@lingui/react/macro";
import Link from "@/components/core/localized-link";
import { Box, Typography } from "@mui/material";
import { useSeoURLStore } from "@/components/context/seo-lang-store";
import { languageIds } from "@/lib/language";
import { sendGAEventTrigger } from "@/utils/analytics-trigger";
import {
  stickCustomHTML,
  filterLayouts,
  handleNonListHTML,
} from "@/components/core/parse-custom-html";
import { postRequest } from "@/utils/request";
import XNoticeContent from "@/main/_components/xnotice-content/xnotice-content";
import { ApiResponse } from "@/types/api";

interface IInformationData {
  bottom: string;
  description: string;
  information_id: string;
  language_id: string;
  seo_url: { [key: string]: string }[];
  status: string;
  store: string;
  layout: any;
}

interface IAddToCartParams {
  product_id: string;
  quantity: number;
}

const InformationPage = ({ lang, data }: { lang: string; data: ApiResponse<IInformationData> }) => {
  const { setSeoLinks } = useSeoURLStore();
  const queryClient = useQueryClient();
  const { message, setMessage } = useAlertStore();

  const content = data?.data?.description || "";
  const layout = data.data.layout || [];

  useEffect(() => {
    if (data?.success) {
      const seoUrls: { [key: string]: string } = {};
      const seoData = data.data.seo_url as unknown as { [key: string]: string };
      for (const [key, value] of Object.entries(languageIds)) {
        seoUrls[key] = seoData[value];
      }
      setSeoLinks(seoUrls);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [productName, setProductName] = useState<string>("");
  const [productSeo, setProductSeo] = useState<string>("");

  const addToCartMutation = useMutation({
    mutationFn: ({ product_id, quantity }: IAddToCartParams) =>
      postRequest({
        path: { name: "cart" },
        body: { product_id, quantity, option: {} },
        lang,
      }).then(() => ({
        product_id,
        quantity,
      })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cartQuantity"] });
      setMessage({
        text: (
          <Trans>
            Success: You have added
            <Link href={`/product/${productSeo}`} data-testid="alert-login" prefetch={false}>
              <Typography
                component="span"
                sx={{
                  px: "4px",
                  fontSize: "14px",
                  color: "primary.main",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {productName}
              </Typography>
            </Link>
            to your
            <Link href="/checkout" data-testid="alert-cart" prefetch={false}>
              <Typography
                component="span"
                sx={{
                  px: "4px",
                  fontSize: "14px",
                  color: "primary.main",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                shopping cart!
              </Typography>
            </Link>
          </Trans>
        ),
        severity: "success",
      });
    },
  });

  const handleAddToCart = (productId: string, quantity: number) => {
    addToCartMutation.mutate({ product_id: productId, quantity });
    sendGAEventTrigger("Add To Cart", {
      send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
      category: "ecommerce",
      label: productName,
    });
  };

  const handleButtonClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const button = target.closest("button[data-product-id]");
    if (button) {
      const productId = button.getAttribute("data-product-id");
      const quantity = button.getAttribute("data-quantity");
      const title = button.getAttribute("data-title");
      const productSeo = button.getAttribute("data-product-seo");

      const productId2 = button.getAttribute("data-product-2-id");
      const quantity2 = button.getAttribute("data-2-quantity");
      const title2 = button.getAttribute("data-2-title");
      const productSeo2 = button.getAttribute("data-product-2-seo");
      setProductName(title ?? "");
      setProductSeo(productSeo ?? "");
      if (productId && quantity) {
        handleAddToCart(productId, Number(quantity));
      }
      setProductName(title2 ?? "");
      setProductSeo(productSeo2 ?? "");
      if (productId2 && quantity2) {
        handleAddToCart(productId2, Number(quantity2));
      }
    }
  };
  useEffect(() => {
    if (data?.data?.description) {
      // Set the content without styles and scripts in the container
      const container = document.getElementById("dynamic-content-container");
      // Event delegation for buttons

      if (container) {
        container.addEventListener("click", handleButtonClick);
      }

      // Cleanup on unmount
      return () => {
        if (container) {
          container.removeEventListener("click", handleButtonClick);
        }
      };
    }
    // Cleanup function to remove the style when the component unmounts
    return () => {
      const styleElement = document.getElementById("dynamic-styles");
      if (styleElement) {
        document.head.removeChild(styleElement); // Remove the style from the document head
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const topSection = filterLayouts(layout?.content_top ? layout?.content_top : []);
  const bottomSection = filterLayouts(layout?.content_bottom ? layout?.content_bottom : []);
  return (
    <>
      {message && <AlertComponent message={message} setMessage={setMessage} />}
      {topSection.length > 0 && (
        <Box className="text-sm [&_ul]:list-disc [&_ul]:pl-5">
          {topSection.map((module) => stickCustomHTML(module))}
        </Box>
      )}
      <XNoticeContent settings={{ selector: ".heading" }} />
      <Box
        sx={{
          "& ul": {
            listStyleType: "disc",
          },
          "& li": {
            marginBottom: "0.5em",
          },
        }}
        id="dynamic-content-container"
        className="mb-4 mt-[5rem] flex min-h-[600px] flex-col gap-2.5 text-sm md:mt-0"
      >
        {handleNonListHTML(content)}
      </Box>
      {bottomSection.length > 0 && (
        <Box className="text-sm [&_ul]:list-disc [&_ul]:pl-5">
          {bottomSection.map((module) => stickCustomHTML(module))}
        </Box>
      )}
    </>
  );
};

export default InformationPage;
